/**
 * Creates a plain Redux action creator.
 *
 * This is a lightweight replacement for the `redux-action` package,
 * which was removed to eliminate its transitive dependency on the
 * vulnerable `uuid@3.4.0` (CVE-2026-41907).
 *
 * Mirrors the redux-actions `createAction` API:
 *   - Calling the returned function with an optional payload returns
 *     a plain `{ type, payload }` object — no thunk, no middleware required.
 *   - `.type` and `.toString()` expose the action type string so the creator
 *     can be used directly in reducer `handleAction(s)` calls.
 *
 * @param type - The action type string.
 * @returns A plain action creator `(payload?: any) => { type, payload }`.
 */
export default function createAction(type: string) {
  // `payload` is the value passed by the caller; `{ type, payload }` uses ES6
  // shorthand, so the returned object is always `{ type: type, payload: payload }`.
  const fn = (payload?: any): { type: string; payload: any } => ({ type, payload });

  fn.type = type;
  fn.toString = () => type;

  return fn;
}
